import type { TopUpPaymentEnvelope } from '../services/walletService';

/**
 * A recharge in flight: recorded just before we redirect the browser to the Redsys
 * hosted page, so the return route (UrlOK / UrlKO) — which carries no context of its
 * own — knows which Brand's saldo to poll and what the balance was before paying.
 * Persisted in `localStorage` so it survives the full-page navigation away to the bank
 * and back (and an app reload on return).
 */
export interface PendingOnlineTopUp {
    brandId: string;
    /** Amount (€) the User chose to recharge. */
    amount: number;
    /** Redsys `Ds_Order` for reference. */
    order: string;
    /** Purse total balance (€) captured before paying — the baseline the return page polls past. */
    baselineTotal: number;
    /** Epoch ms when the recharge was started, to discard stale records. */
    startedAt: number;
}

const STORAGE_KEY = 'zovy.pendingOnlineTopUp';

/** Records for longer than this are treated as stale and ignored. */
const MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

export function savePendingOnlineTopUp(pending: PendingOnlineTopUp): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
    } catch {
        // Best-effort: without this the return page falls back to a generic message.
    }
}

export function readPendingOnlineTopUp(): PendingOnlineTopUp | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        if (
            parsed &&
            typeof parsed === 'object' &&
            typeof parsed.brandId === 'string' &&
            typeof parsed.amount === 'number' &&
            typeof parsed.order === 'string' &&
            typeof parsed.baselineTotal === 'number' &&
            typeof parsed.startedAt === 'number'
        ) {
            if (Date.now() - parsed.startedAt > MAX_AGE_MS) {
                clearPendingOnlineTopUp();
                return null;
            }
            return parsed as PendingOnlineTopUp;
        }
    } catch {
        // Corrupt payload — treat as no pending recharge.
    }
    return null;
}

export function clearPendingOnlineTopUp(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Ignore — nothing we can do, and it doesn't block the flow.
    }
}

/**
 * Send the User to the Redsys hosted payment page by auto-submitting a POST form with the
 * signed envelope. Redsys expects the exact `Ds_*` field names; the values come from the
 * backend. No card data is ever collected in the app — it is all entered on Redsys's page.
 */
export function redirectToRedsys(envelope: TopUpPaymentEnvelope): void {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = envelope.redsysUrl;
    form.style.display = 'none';

    const fields: Record<string, string> = {
        Ds_SignatureVersion: envelope.signatureVersion,
        Ds_MerchantParameters: envelope.merchantParameters,
        Ds_Signature: envelope.signature,
    };

    for (const [name, value] of Object.entries(fields)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
}
